import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Mail, Sparkles, CheckCircle2, ArrowLeft } from 'lucide-react';

interface AuthShowcaseProps {
  onNavigate: (screen: string) => void;
}

export function AuthShowcase({ onNavigate }: AuthShowcaseProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <Button
            variant="ghost"
            onClick={() => onNavigate('landing')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-4xl mb-4">Authentication System Showcase 🎁</h1>
          <p className="text-muted-foreground text-lg">
            Beautiful, secure magic link authentication with professional email templates
          </p>
        </div>

        <Tabs defaultValue="overview" className="space-y-8">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="email">Email Template</TabsTrigger>
            <TabsTrigger value="flow">User Flow</TabsTrigger>
            <TabsTrigger value="api">API</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Authentication Features ✨</CardTitle>
                <CardDescription>
                  A complete, production-ready authentication system
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Magic Link Authentication
                    </h3>
                    <ul className="text-sm space-y-1 text-muted-foreground ml-6">
                      <li>• Passwordless sign-in via email or phone</li>
                      <li>• 15-minute token expiration</li>
                      <li>• Secure JWT session tokens</li>
                      <li>• localStorage persistence</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Beautiful Email Design
                    </h3>
                    <ul className="text-sm space-y-1 text-muted-foreground ml-6">
                      <li>• Professional gradient header</li>
                      <li>• Responsive mobile design</li>
                      <li>• Clear call-to-action buttons</li>
                      <li>• Security warnings included</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Smart User Flow
                    </h3>
                    <ul className="text-sm space-y-1 text-muted-foreground ml-6">
                      <li>• Automatic email/phone detection</li>
                      <li>• New user registration handling</li>
                      <li>• Birthday validation (13+ years)</li>
                      <li>• Toast notifications</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      Production Ready
                    </h3>
                    <ul className="text-sm space-y-1 text-muted-foreground ml-6">
                      <li>• Live Cloudflare Worker backend</li>
                      <li>• Bird API email service</li>
                      <li>• D1 database integration</li>
                      <li>• KV storage for tokens</li>
                    </ul>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button onClick={() => onNavigate('login')} className="bg-emerald-600">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Try Authentication
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="email" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Email Template Preview 📧</CardTitle>
                <CardDescription>
                  Professional, mobile-responsive email design
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-100 p-8 rounded-lg">
                  <div className="max-w-xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
                    {/* Email Preview */}
                    <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-400 p-10 text-center">
                      <div className="text-5xl mb-3">🎁</div>
                      <h1 className="text-white text-2xl mb-2">Better to Give than Receive</h1>
                      <p className="text-white/90 text-sm">Collaborative Gift-Giving Made Easy</p>
                    </div>

                    <div className="p-8 space-y-4">
                      <h2 className="text-xl font-semibold text-gray-900">
                        Your Sign-In Link is Ready! 🎉
                      </h2>
                      
                      <p className="text-gray-600 text-sm leading-relaxed">
                        We received a request to sign in to your BtGtR account. Click the button below to securely access your account:
                      </p>

                      <div className="text-center py-4">
                        <div className="inline-block bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-8 py-3 rounded-lg shadow-md">
                          ✨ Sign In to BtGtR
                        </div>
                      </div>

                      <div className="border-t pt-4 mt-4">
                        <p className="text-gray-600 text-xs mb-2">
                          Or copy and paste this link:
                        </p>
                        <div className="bg-gray-50 border border-gray-200 rounded p-2 text-xs text-emerald-600 break-all">
                          https://btgtr.app/verify?token=magic_xxxxx...
                        </div>
                      </div>

                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded">
                        <p className="text-yellow-900 text-xs">
                          <strong>⏱️ This link expires in 15 minutes</strong> for your security.
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-6 text-center border-t">
                      <p className="text-emerald-600 text-sm italic mb-2">
                        "It is more blessed to give than to receive."
                      </p>
                      <p className="text-gray-500 text-xs mb-3">Acts 20:35</p>
                      <p className="text-gray-400 text-xs">
                        © 2024-2025 Better to Give than Receive
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Badge>Mobile Responsive</Badge>
                    <p className="text-sm text-muted-foreground">
                      Looks great on all devices and email clients
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Badge>Brand Consistent</Badge>
                    <p className="text-sm text-muted-foreground">
                      Matches BtGtR festive design system
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Badge>Accessible</Badge>
                    <p className="text-sm text-muted-foreground">
                      Clear hierarchy and readable text
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="flow" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Authentication Flow 🔄</CardTitle>
                <CardDescription>
                  Step-by-step user journey
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-emerald-600">1</span>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold">User Enters Contact</h3>
                      <p className="text-sm text-muted-foreground">
                        User visits landing page and clicks "Get Started". They enter their email or phone number with real-time validation.
                      </p>
                      <Badge variant="outline">LoginScreen Component</Badge>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-emerald-600">2</span>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold">Magic Link Sent</h3>
                      <p className="text-sm text-muted-foreground">
                        Backend generates unique token, stores in KV, and sends beautiful email via Bird API. User sees success message.
                      </p>
                      <Badge variant="outline">POST /auth/request-magic-link</Badge>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-emerald-600">3</span>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold">User Clicks Email Link</h3>
                      <p className="text-sm text-muted-foreground">
                        Email arrives with festive design. User clicks button, redirected to app with token in URL.
                      </p>
                      <Badge variant="outline">https://btgtr.app/verify?token=...</Badge>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-emerald-600">4</span>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold">Token Verification</h3>
                      <p className="text-sm text-muted-foreground">
                        App extracts token, calls verify endpoint. Backend validates token, checks user exists, returns session token.
                      </p>
                      <Badge variant="outline">POST /auth/verify-magic-link</Badge>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-blue-600">5a</span>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold">New User Registration</h3>
                      <p className="text-sm text-muted-foreground">
                        If user doesn't exist, they're prompted to complete registration with birthdate. After registering, they request a new magic link.
                      </p>
                      <Badge variant="outline">RegisterScreen Component</Badge>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-emerald-600">5b</span>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold">Existing User Sign In</h3>
                      <p className="text-sm text-muted-foreground">
                        Session token stored in localStorage. User redirected to dashboard. Token included in all subsequent API calls.
                      </p>
                      <Badge variant="outline">Authorization: Bearer {'{token}'}</Badge>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <Button onClick={() => onNavigate('login')}>
                    <Mail className="w-4 h-4 mr-2" />
                    Experience the Flow
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="api" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>API Endpoints 🔌</CardTitle>
                <CardDescription>
                  Live backend at btgtr.digeratidavex.workers.dev
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-green-600">POST</Badge>
                      <code className="text-sm">/api/v1/auth/request-magic-link</code>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Request a magic link via email or SMS
                    </p>
                    <div className="bg-gray-50 rounded p-3 text-xs font-mono">
                      <div className="text-gray-500">Request:</div>
                      {`{
  "contact": "user@example.com",
  "context": "dashboard"
}`}
                    </div>
                    <div className="bg-gray-50 rounded p-3 text-xs font-mono mt-2">
                      <div className="text-gray-500">Response:</div>
                      {`{
  "success": true,
  "message": "Magic link sent",
  "isExistingUser": false,
  "requiresBirthdate": true
}`}
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-green-600">POST</Badge>
                      <code className="text-sm">/api/v1/auth/verify-magic-link</code>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Verify a magic link token
                    </p>
                    <div className="bg-gray-50 rounded p-3 text-xs font-mono">
                      <div className="text-gray-500">Request:</div>
                      {`{
  "token": "magic_xxxxx"
}`}
                    </div>
                    <div className="bg-gray-50 rounded p-3 text-xs font-mono mt-2">
                      <div className="text-gray-500">Response:</div>
                      {`{
  "success": true,
  "sessionToken": "session_xxxxx",
  "userId": "user_xxxxx",
  "contact": "user@example.com",
  "contactType": "email"
}`}
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-green-600">POST</Badge>
                      <code className="text-sm">/api/v1/auth/register</code>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Register a new user
                    </p>
                    <div className="bg-gray-50 rounded p-3 text-xs font-mono">
                      <div className="text-gray-500">Request:</div>
                      {`{
  "contact": "user@example.com",
  "birthdate": "1990-01-15"
}`}
                    </div>
                    <div className="bg-gray-50 rounded p-3 text-xs font-mono mt-2">
                      <div className="text-gray-500">Response:</div>
                      {`{
  "success": true,
  "message": "User registered",
  "userId": "user_xxxxx"
}`}
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-blue-600">GET</Badge>
                      <code className="text-sm">/api/v1/users/profile</code>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      Get authenticated user's profile (requires session token)
                    </p>
                    <div className="bg-gray-50 rounded p-3 text-xs font-mono">
                      <div className="text-gray-500">Headers:</div>
                      {`Authorization: Bearer session_xxxxx`}
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 mb-2">Testing with curl</h3>
                  <p className="text-sm text-blue-800 mb-3">
                    You can test these endpoints directly:
                  </p>
                  <div className="bg-white rounded p-3 text-xs font-mono overflow-x-auto">
                    {`curl -X POST https://btgtr.digeratidavex.workers.dev/api/v1/auth/request-magic-link \\
  -H "Content-Type: application/json" \\
  -d '{"contact":"your.email@example.com","context":"dashboard"}'`}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
